import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import { breakpoint } from '../../../../__styling/style-functions/breakpoint'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
interface JabTrackerBase {
    type?: 'small' | 'large'
}
interface StyledJabTrackerProps extends JabTrackerBase {
    containerRatios: {
        desktop: string
        mobile: string
        tablet: string
        fallbackWidth: number
    }
    isLoading?: boolean
}
interface DifferenceInCasesProps extends JabTrackerBase {
    direction: 'up' | 'down'
}

interface DotIconProps {
    color: 'blue' | 'green' | 'red'
}
interface StyledFlexTableProps extends JabTrackerBase {}
interface StyledVaxBarProps extends JabTrackerBase {}
interface StyledVaxGridProps extends JabTrackerBase {}
interface StyledTitleGridItemProps extends JabTrackerBase {}
interface StyledTotalCasesProps extends JabTrackerBase {}

const grey = '#cccccc'
const darkGrey = '#1B262E'
const borderGrey = '#999999'
const blue = '#009FDA'
const darkBlue = '#2C4A60'
const green = '#7DEFB9'
const red = '#FF5151'
const white = '#ffffff'

export const StyledJabTrackerContainer = styled(
    'section',
)<StyledJabTrackerProps>(({ type, containerRatios, theme, isLoading }) => ({
    fontFamily: theme.fonts.heading,
    border: '1px solid black',
    display: type === 'large' ? 'none' : 'grid',
    height: 'auto',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(3, auto)',
    backgroundColor: darkGrey,
    color: white,
    padding: type === 'large' ? calcRem(15) : calcRem(10),
    marginBottom: type === 'large' ? 0 : calcRem(5),
    overflow: 'hidden',
    [breakpoint('sm')]: {
        display: type === 'large' ? 'grid' : 'none',
    },
    [breakpoint('md')]: {
        margin: calcRem(30, 0),
        display:
            containerRatios.fallbackWidth > 900 && type === 'large'
                ? 'grid'
                : containerRatios.fallbackWidth < 900 && type === 'large'
                ? 'none'
                : containerRatios.fallbackWidth > 900 && type === 'small'
                ? 'none'
                : containerRatios.fallbackWidth < 900 && type === 'small'
                ? 'grid'
                : 'none',
    },
}))

export const StyledGridItem = styled('div')({
    //border: '1px solid red',
})

export const StyledTitleGridItem = styled(
    StyledGridItem,
)<StyledTitleGridItemProps>(({ type }) => ({
    display: 'flex',
    fontWeight: 700,
    position: 'relative',
    flexDirection: type === 'large' ? 'row' : 'column',
    ['h3, p']: {
        margin: '0',
    },
    ['h3']: {
        fontSize: calcRem(22),
    },
    ['p']: {
        fontSize: calcRem(12),
        display: type === 'large' ? 'block' : 'flex',
        flexDirection: type === 'large' ? 'row' : 'column',
        marginBottom: type === 'large' ? 0 : calcRem(5),
    },
    ['span:first-child']: {
        textTransform: 'uppercase',
    },
    ['.StyledTitleGridItem--total-case-wrap']: {
        display: 'flex',
        justifyContent: type === 'large' ? 'center' : 'flex-start',
        marginLeft: type === 'large' ? calcRem(25) : '0',
    },
}))

export const StyledTotalCases = styled('div')<StyledTotalCasesProps>(
    ({ type }) => ({
        backgroundColor: darkBlue,
        borderRadius: '10px',
        fontSize: '18px',
        fontWeight: 700,
        color: white,
        textTransform: 'uppercase',
        padding: type === 'large' ? calcRem(8, 15) : calcRem(2, 10),
        display: 'flex',
        alignSelf: 'flex-end',
        ['.totalCases--value']: {
            color: grey,
            margin: calcRem(0, 10),
            fontWeight: 700,
        },

        ['.totalCases--total']: {
            textTransform: 'uppercase',
        },
    }),
)

export const DifferenceInCases = styled('span')<DifferenceInCasesProps>(
    ({ direction, type }) => ({
        color: direction === 'up' ? red : blue,
        alignSelf: 'flex-start',
        marginLeft: type === 'large' ? calcRem(8) : 0,
    }),
)

export const StyledSyringeImg = styled('img')({
    height: 'auto',
    top: '-38px',
    right: '-11px',
    position: 'absolute',
})

export const StyledGridTable = styled('div')<StyledFlexTableProps>(
    ({ type }) => ({
        display: 'grid',
        gridTemplateColumns:
            type === 'large' ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
        borderBottom: `1px solid ${borderGrey}`,
        padding: calcRem(15, 0),
        ['.StyledGridTable--FlexCell']: {
            display: 'flex',
            fontWeight: 700,
        },
        ['.StyledGridTable--StateKey']: {
            minWidth: type === 'large' ? calcRem(45) : calcRem(35),
        },
        ['.StyledGridTable--Number']: {
            color: grey,
        },
    }),
)

interface StyledVaxBarIndicatorProps {
    percent?: number
}
export const StyledVaxBarWrap = styled('div')({
    borderBottom: `1px solid ${borderGrey}`,
    paddingBottom: calcRem(10),
    marginBottom: calcRem(8),
    h3: {
        fontSize: calcRem(18),
        textTransform: 'uppercase',
        fontWeight: 600,
        margin: calcRem(8, 0),
    },
})
export const StyledVaxBar = styled('div')<StyledVaxBarProps>(({ type }) => ({
    height: '10px',
    width: '100%',
    display: 'flex',
    borderRadius: '3px',

    [`${StyledVaxBarIndicator}`]: {
        minWidth: type === 'large' ? '60px' : '30px',
    },
}))

export const StyledVaxBarIndicator = styled(
    motion.div,
)<StyledVaxBarIndicatorProps>(({ theme }) => ({
    height: '100%',
    width: `0%`,

    '&:nth-child(1)': {
        background: blue,
        borderRadius: '3px 0 0 3px',
        position: 'relative',

        '&:after': {
            content: `''`,
            position: 'absolute',
            height: '100%',
            width: '3px',
            background: blue,
            right: '-2px',
            zIndex: 0,
        },
    },
    '&:nth-child(2)': {
        background: green,
        borderRadius: '3px',
        position: 'relative',
        zIndex: 1,
    },
    '&:nth-child(3)': {
        background: red,
        borderRadius: '0 3px 3px 0',
        position: 'relative',
        '&::after': {
            content: `''`,
            position: 'absolute',
            height: '100%',
            width: '3px',
            background: red,
            left: '-2px',
            zIndex: 0,
        },
    },
}))

export const StyledVaxGrid = styled('div')<StyledVaxGridProps>(({ type }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    fontWeight: 700,
    fontSize: calcRem(22),
    maxWidth: type === 'large' ? '50%' : 'unset',
}))

export const StyledVaxGridItem = styled('div')({
    paddingRight: calcRem(10),
})

export const DotIcon = styled('div')<DotIconProps>(({ color }) => ({
    height: calcRem(8),
    width: calcRem(8),
    borderRadius: '50%',
    marginRight: calcRem(5),
    position: 'relative',
    backgroundColor: color === 'blue' ? blue : color === 'green' ? green : red,
}))

export const StyledVaxTitle = styled('div')({
    display: 'flex',
    alignItems: 'center',
    fontSize: calcRem(12),
})

export const StyledSource = styled('span')(({ theme }) => ({
    fontSize: calcRem(14),
    color: grey,
    a: {
        textDecoration: 'none',
        color: theme.colors.actions.link.hover,
    },
}))
